import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiCalendar, FiUser, FiClock, FiArrowRight, FiTag } from 'react-icons/fi';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import useGSAPScrollAnimations from '../hooks/useGSAPScrollAnimations';
import Header from '../components/Header';
import Footer from '../components/Footer';

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

const Blog = () => {
  const sectionRef = useRef(null);
  const heroRef = useRef(null);
  const postsRef = useRef(null);
  
  const { fadeInUp, staggerIn, parallax } = useGSAPScrollAnimations();

  useEffect(() => {
    // Hero section animation
    if (heroRef.current) {
      const heroElements = heroRef.current.children;
      staggerIn(Array.from(heroElements), { 
        stagger: 0.2,
        y: 60,
        delay: 0.3 
      });
    }
    
    // Blog posts animation
    if (postsRef.current) {
      const postCards = postsRef.current.querySelectorAll('.blog-card');
      Array.from(postCards).forEach((card, index) => {
        gsap.fromTo(card,
          {
            y: 80,
            opacity: 0,
            scale: 0.9
          },
          {
            y: 0,
            opacity: 1,
            scale: 1,
            duration: 1,
            ease: "power2.out",
            scrollTrigger: {
              trigger: card,
              start: "top 85%",
              end: "bottom 15%",
              toggleActions: "play none none reverse"
            },
            delay: index * 0.1
          }
        );
      });
    }

    // Parallax background elements
    const backgroundElements = sectionRef.current?.querySelectorAll('.parallax-bg');
    if (backgroundElements) {
      backgroundElements.forEach((element, index) => {
        parallax(element, { 
          yPercent: index % 2 === 0 ? -30 : 30,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top bottom",
            end: "bottom top",
            scrub: 1
          }
        });
      });
    }
  }, [fadeInUp, staggerIn, parallax]);

  const blogPosts = [
    {
      id: 1,
      title: "The Future of AI Integration: Building Tomorrow's Solutions Today",
      excerpt: "Exploring how AI tools are revolutionizing workflows and what it means for the future of productivity and innovation.",
      content: "Artificial Intelligence is no longer a distant concept—it's here, transforming how we work, create, and solve problems. At Bervin, we're at the forefront of this revolution, building an integrated platform that makes powerful AI tools accessible to everyone.",
      author: "Beryl Gabriel",
      date: "2024-12-15",
      readTime: "8 min read",
      category: "AI Technology",
      tags: ["AI", "Technology", "Innovation", "Future"],
      image: "/api/placeholder/800/400",
      featured: true
    },
    {
      id: 2,
      title: "Democratizing AI: Making Advanced Tools Accessible to Everyone",
      excerpt: "How we're breaking down barriers and bringing powerful AI capabilities to users regardless of technical expertise.",
      content: "The democratization of AI is not just a trend—it's a necessity. We believe that powerful AI tools shouldn't be limited to tech giants or require extensive technical knowledge to use effectively.",
      author: "Jivin Michaeswaran",
      date: "2024-12-10",
      readTime: "6 min read",
      category: "Product",
      tags: ["Accessibility", "AI", "Product", "User Experience"],
      image: "/api/placeholder/800/400",
      featured: false
    },
    {
      id: 3,
      title: "Building Seamless User Experiences in AI Applications",
      excerpt: "The design principles and methodologies we use to create intuitive AI-powered interfaces.",
      content: "User experience in AI applications requires a delicate balance between powerful functionality and intuitive design. At Bervin, we've developed design principles that make complex AI capabilities feel natural and accessible.",
      author: "Beryl Gabriel",
      date: "2024-12-05",
      readTime: "5 min read",
      category: "Design",
      tags: ["UX", "Design", "AI", "Interface"],
      image: "/api/placeholder/800/400",
      featured: false
    },
    {
      id: 4,
      title: "The Technical Architecture Behind Bervin's AI Platform",
      excerpt: "A deep dive into the scalable infrastructure that powers our unified AI ecosystem.",
      content: "Building a platform that can handle multiple AI tools requires careful architectural planning and robust infrastructure. Here's how we've built Bervin to be scalable, reliable, and fast.",
      author: "Jivin Michaeswaran",
      date: "2024-11-28",
      readTime: "12 min read",
      category: "Technical",
      tags: ["Architecture", "Infrastructure", "Technical", "Scalability"],
      image: "/api/placeholder/800/400",
      featured: false
    },
    {
      id: 5,
      title: "AI Ethics and Responsible Development at Bervin",
      excerpt: "Our commitment to developing AI solutions that are ethical, transparent, and beneficial for society.",
      content: "As AI becomes more powerful, the responsibility to develop it ethically becomes paramount. At Bervin, we're committed to building AI that augments human capabilities while respecting privacy and promoting fairness.",
      author: "Beryl Gabriel",
      date: "2024-11-20",
      readTime: "7 min read",
      category: "Ethics",
      tags: ["Ethics", "Responsibility", "AI", "Society"],
      image: "/api/placeholder/800/400",
      featured: false
    },
    {
      id: 6,
      title: "From Concept to Launch: The Bervin Journey",
      excerpt: "The story behind Bervin's creation and our vision for the future of AI accessibility.",
      content: "Every great platform starts with an idea. For Bervin, that idea was simple yet ambitious: make powerful AI tools accessible to everyone, regardless of their technical background.",
      author: "Beryl Gabriel & Jivin Michaeswaran",
      date: "2024-11-15",
      readTime: "10 min read",
      category: "Company",
      tags: ["Journey", "Company", "Vision", "Founders"],
      image: "/api/placeholder/800/400",
      featured: false
    }
  ];

  const categories = ["All", "AI Technology", "Product", "Design", "Technical", "Ethics", "Company"];
  const [selectedCategory, setSelectedCategory] = React.useState("All");

  const filteredPosts = selectedCategory === "All" 
    ? blogPosts 
    : blogPosts.filter(post => post.category === selectedCategory);

  const featuredPost = blogPosts.find(post => post.featured);

  return (
    <div className="min-h-screen bg-dark-900">
      <Header />
      
      <main ref={sectionRef} className="relative overflow-hidden">
        {/* Background Elements with Parallax */}
        <div className="absolute inset-0 opacity-30">
          <div className="parallax-bg absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-r from-secondary-500/20 to-accent-500/20 rounded-full blur-3xl"></div>
          <div className="parallax-bg absolute bottom-1/4 right-1/4 w-80 h-80 bg-gradient-to-r from-accent-500/20 to-primary-500/20 rounded-full blur-3xl"></div>
        </div>

        {/* Hero Section */}
        <section className="py-24 px-6 relative z-10">
          <div className="container mx-auto">
            <div ref={heroRef} className="text-center mb-16">
              <h1 className="text-5xl md:text-7xl font-display font-bold mb-6">
                <span className="gradient-text">Bervin Blog</span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
                Insights, updates, and thoughts on AI, technology, and the future of innovation from the Bervin team.
              </p>
            </div>
          </div>
        </section>

        {/* Featured Post */}
        {featuredPost && (
          <section className="py-12 px-6 relative z-10">
            <div className="container mx-auto">
              <div className="mb-12">
                <h2 className="text-3xl md:text-4xl font-display font-bold mb-8 text-center">
                  <span className="gradient-text">Featured Article</span>
                </h2>
                
                <motion.div
                  initial={{ opacity: 0, y: 60 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  className="relative group"
                >
                  <div className="relative h-full p-8 glass border border-white/10 rounded-3xl overflow-hidden hover-lift">
                    {/* Background Gradient */}
                    <div className="absolute inset-0 bg-gradient-to-br from-secondary-500/10 to-accent-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                      {/* Content */}
                      <div className="relative z-10">
                        <div className="flex items-center gap-4 mb-4">
                          <span className="px-3 py-1 bg-gradient-to-r from-secondary-500 to-accent-500 rounded-full text-sm font-medium text-white">
                            {featuredPost.category}
                          </span>
                          <span className="text-accent-400 font-medium text-sm">Featured</span>
                        </div>
                        
                        <h3 className="text-3xl md:text-4xl font-bold text-white mb-4 group-hover:text-accent-400 transition-colors duration-300">
                          {featuredPost.title}
                        </h3>
                        
                        <p className="text-gray-300 text-lg leading-relaxed mb-6">
                          {featuredPost.excerpt}
                        </p>
                        
                        <div className="flex items-center gap-6 text-sm text-gray-400 mb-6">
                          <div className="flex items-center gap-2">
                            <FiUser className="w-4 h-4" />
                            <span>{featuredPost.author}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <FiCalendar className="w-4 h-4" />
                            <span>{new Date(featuredPost.date).toLocaleDateString()}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <FiClock className="w-4 h-4" />
                            <span>{featuredPost.readTime}</span>
                          </div>
                        </div>
                        
                        <Link to={`/blog/${featuredPost.id}`}>
                          <motion.button
                            whileHover={{ scale: 1.05, x: 5 }}
                            whileTap={{ scale: 0.95 }}
                            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-secondary-500 to-accent-500 rounded-xl text-white font-medium hover:shadow-lg hover:shadow-accent-500/25 transition-all duration-300"
                          >
                            Read Full Article
                            <FiArrowRight className="w-4 h-4" />
                          </motion.button>
                        </Link>
                      </div>
                      
                      {/* Image */}
                      <div className="relative">
                        <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-gradient-to-br from-secondary-500/20 to-accent-500/20 flex items-center justify-center">
                          <div className="text-6xl font-bold text-white/20">
                            AI
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </section>
        )}

        {/* Category Filter */}
        <section className="py-8 px-6 relative z-10">
          <div className="container mx-auto">
            <div className="flex flex-wrap justify-center gap-4">
              {categories.map((category) => (
                <motion.button
                  key={category}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 ${
                    selectedCategory === category
                      ? 'bg-gradient-to-r from-secondary-500 to-accent-500 text-white shadow-lg'
                      : 'bg-white/5 text-gray-300 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  {category}
                </motion.button>
              ))}
            </div>
          </div>
        </section>

        {/* Blog Posts Grid */}
        <section className="py-12 px-6 relative z-10">
          <div className="container mx-auto">
            <div ref={postsRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.filter(post => !post.featured).map((post) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 60 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="blog-card group relative"
                >
                  {/* Card */}
                  <div className="relative h-full p-6 glass border border-white/10 rounded-2xl overflow-hidden hover-lift">
                    {/* Background Gradient */}
                    <div className="absolute inset-0 bg-gradient-to-br from-secondary-500/5 to-accent-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    
                    {/* Image Placeholder */}
                    <div className="relative mb-6">
                      <div className="aspect-[16/9] rounded-xl overflow-hidden bg-gradient-to-br from-secondary-500/20 to-accent-500/20 flex items-center justify-center">
                        <div className="text-3xl font-bold text-white/20">
                          {post.category}
                        </div>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="relative z-10">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="px-3 py-1 bg-white/10 rounded-full text-xs font-medium text-gray-300">
                          {post.category}
                        </span>
                      </div>
                      
                      <h3 className="text-xl font-bold text-white mb-3 group-hover:text-accent-400 transition-colors duration-300 line-clamp-2">
                        {post.title}
                      </h3>
                      
                      <p className="text-gray-300 leading-relaxed mb-4 line-clamp-3">
                        {post.excerpt}
                      </p>
                      
                      <div className="flex items-center gap-4 text-xs text-gray-400 mb-4">
                        <div className="flex items-center gap-1">
                          <FiUser className="w-3 h-3" />
                          <span>{post.author}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <FiCalendar className="w-3 h-3" />
                          <span>{new Date(post.date).toLocaleDateString()}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <FiClock className="w-3 h-3" />
                          <span>{post.readTime}</span>
                        </div>
                      </div>
                      
                      <div className="flex flex-wrap gap-2 mb-4">
                        {post.tags.slice(0, 3).map((tag) => (
                          <span
                            key={tag}
                            className="flex items-center gap-1 px-2 py-1 bg-white/5 rounded-md text-xs text-gray-400"
                          >
                            <FiTag className="w-3 h-3" />
                            {tag}
                          </span>
                        ))}
                      </div>
                      
                      <Link to={`/blog/${post.id}`}>
                        <motion.button
                          whileHover={{ scale: 1.05, x: 3 }}
                          whileTap={{ scale: 0.95 }}
                          className="inline-flex items-center gap-2 text-accent-400 hover:text-accent-300 font-medium text-sm transition-colors duration-300"
                        >
                          Read More
                          <FiArrowRight className="w-4 h-4" />
                        </motion.button>
                      </Link>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Blog;
